import {JSXElementConstructor, ReactElement} from "react";
import {FormFields} from "../../../../models/forms/form-field.interface";
import {IBookingSearchPagination} from "../../../airline-webapi/requests/booking-search.requests";
import {IBookingSearchResultRecord} from "../../../airline-webapi/responses/booking-search.response";
import {BookingSearchStrategyBase} from "../booking-search-strategy-base";
import {BookingSearchStrategyType} from "../booking-search.strategy.interface";
import {Check} from "../../../../types/type-checking";
import {RecentBookingsFieldsComponent} from "./recent-bookings-fields.component";
import {IRecentBookingsRequest} from "../../../airline-webapi/requests/recent-bookings.request";
import {NullableString} from "../../../../types/nullable-types";
import {albumsOutline} from "ionicons/icons";

interface IRecentlyBookingsFields {
    topCount: number;
}

const DEFAULT_TOP_COUNT = 50;

export class RecentBookingsStrategy extends BookingSearchStrategyBase<IRecentlyBookingsFields, IRecentlyBookingsFields> {
    get type(): BookingSearchStrategyType {
        return BookingSearchStrategyType.RecentBookings;
    }
    get name(): string {
        return this.services.language.translate("Recent bookings");
    }

    get resultsDescription(): string {
        return this.name;
    }

    protected _getIonIconName(): NullableString {
        return albumsOutline;
    }

    protected _createFilterFields(persistedFields: IRecentlyBookingsFields | null): FormFields<IRecentlyBookingsFields> {
        return {
            topCount: this._createField<number>({
                fieldName: () => this.services.language.translate("Records count"),
                defaultValue: persistedFields?.topCount || DEFAULT_TOP_COUNT,
                isRequired: true,
                validate: field => {
                    if(Check.isNullOrUndefined(field.value) ) {
                        return null;
                    }

                    if(field.value < 1) {
                        return this.services.language.translate("Minimum value must be 1");
                    }
                    if(field.value > 100) {
                        return this.services.language.translate("Maximum value must be 100");
                    }

                    return null;
                }
            })
        }
    }
    renderSearchFields(): ReactElement<any, string | JSXElementConstructor<any>> {
        return (
            <RecentBookingsFieldsComponent strategy={this}/>
        )
    }
    async _searchBookings(sessionToken: string, pagination: IBookingSearchPagination): Promise<IBookingSearchResultRecord[]> {
        const request: IRecentBookingsRequest = {
            dotRezToken: sessionToken,
            topCount: this.fields?.topCount?.value ?? DEFAULT_TOP_COUNT,
        }

        const response = await this.services.airlineWebapi.agencyRecentBookings(request);
        return response.results;
    }

    protected _collectFieldsValuesForStorage(): IRecentlyBookingsFields {
        return {
            topCount: this.fields.topCount.value ?? DEFAULT_TOP_COUNT,
        }
    }

    clearAllFilters() {
        super.clearAllFilters();
        this.fields.topCount.setValue(DEFAULT_TOP_COUNT);
    }
}